import IconButton from '@/global/components/Button/IconButton'
import { MenuItem, TextField } from '@mui/material'
import {
    BookmarkPlusIcon,
    CheckIcon,
    PencilIcon,
    Trash2Icon,
} from 'lucide-react'
import React from 'react'
import { filtroVazio } from '../hooks/useDefaultFilterModel'
import useFilterPresets from '../hooks/useFilterPresets'
import FilterModelPro, { GridFilterModelPro } from '../utils/filterModelPro'
import { GridColDefPro } from '../utils/types'

export default function FilterPreset({
    onFilterModelChange,
    filterModel,
    columns,
}: {
    onFilterModelChange: React.Dispatch<
        React.SetStateAction<GridFilterModelPro>
    >
    filterModel: GridFilterModelPro
    columns: GridColDefPro[]
}) {
    const {
        filtros,
        adicionarPreset,
        atualizarPreset,
        excluirPreset,
        isLoading,
    } = useFilterPresets({ columns })

    return (
        <TextField
            fullWidth
            variant="outlined"
            select
            disabled={isLoading}
            size="small"
            sx={{
                '& .MuiInputBase-root': {
                    height: '30px',
                },
                '& .MuiFormLabel-root': {
                    fontSize: '0.8rem',
                    lineHeight: '1em',
                },
            }}
            SelectProps={{
                MenuProps: {
                    PaperProps: {
                        sx: {
                            maxHeight: '200px',
                            width: '250px',
                            '& ul': {
                                p: 0,
                                '& a': {
                                    fontSize: '0.95rem',
                                },
                            },
                        },
                    },
                },
            }}
            onChange={e => {
                e.preventDefault()
                e.stopPropagation()
            }}
            value={-1}
        >
            <MenuItem value={-1} sx={{ display: 'none' }}>
                <span style={{ opacity: 0.5, fontSize: '0.85rem' }}>
                    Filtros salvos
                </span>
            </MenuItem>
            {!filterModel.items.length && !filtros.length && (
                <MenuItem value={-2} disabled>
                    <em>Nenhum filtro salvo</em>
                </MenuItem>
            )}
            {!!filterModel.items.length && (
                <MenuItem
                    value={0}
                    onClick={() => adicionarPreset(filterModel.items)}
                    sx={{ width: '100%', height: '40px' }}
                >
                    <BookmarkPlusIcon className="mr-1" />
                    Salvar predefinição atual
                </MenuItem>
            )}
            {filtros.map(filtro => (
                <MenuItem
                    key={filtro.id}
                    value={filtro.id}
                    sx={{ width: '100%', height: '40px' }}
                    onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        new Promise(resolve => {
                            onFilterModelChange(filtroVazio)
                            setTimeout(() => {
                                onFilterModelChange({
                                    ...filtroVazio,
                                    ...new FilterModelPro({
                                        items: filtro.items,
                                    }).filterModel,
                                })
                                resolve(true)
                            }, 100)
                        })
                    }}
                >
                    <ItemEditavel
                        filtro={filtro}
                        atualizarPreset={atualizarPreset}
                    />
                    <IconButton
                        size="small"
                        title="Excluir"
                        sx={{ mr: -2 }}
                        onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()
                            excluirPreset(filtro.id)
                        }}
                    >
                        <Trash2Icon className="text-error" />
                    </IconButton>
                </MenuItem>
            ))}
        </TextField>
    )
}

const ItemEditavel = ({
    filtro,
    atualizarPreset,
}: {
    filtro: any
    atualizarPreset: ReturnType<typeof useFilterPresets>['atualizarPreset']
}) => {
    const [editando, setEditando] = React.useState(false)
    const [nome, setNome] = React.useState(filtro.nome)

    return editando ? (
        <>
            <TextField
                variant="outlined"
                autoFocus
                size="small"
                value={nome}
                sx={{
                    fontSize: '0.85rem',
                    flex: 1,
                    m: 0,
                }}
                onChange={e => setNome(e.target.value)}
                onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        atualizarPreset(filtro.id, nome)
                        setEditando(false)
                    } else {
                        e.stopPropagation()
                    }
                }}
                InputProps={{
                    inputProps: {
                        maxLength: 40,
                    },
                }}
            />
            <IconButton
                size="small"
                title="Salvar"
                sx={{ mr: 0 }}
                color="success"
                onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    atualizarPreset(filtro.id, nome)
                    setEditando(false)
                }}
            >
                <CheckIcon />
            </IconButton>
        </>
    ) : (
        <>
            <span
                title={`Carregar ${filtro.nome}`}
                style={{
                    flex: 1,
                    maxWidth: '160px',
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                {filtro.nome}
            </span>
            <IconButton
                size="small"
                title="Alterar nome"
                sx={{ mr: 0 }}
                color="success"
                onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    setEditando(true)
                }}
            >
                <PencilIcon />
            </IconButton>
        </>
    )
}
